/**
 * Support Page
 * Contact form and support information for Kata.fit
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import './LegalPages.css';
import { API_BASE_URL } from '../config';

const SUPPORT_EMAIL = 'team@kata.fit';

const SupportPage = () => {
  const { user, isLoaded } = useUser();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-populate user info when loaded
  useEffect(() => {
    if (isLoaded && user) {
      setFormData(prev => ({
        ...prev,
        name: user.fullName || user.firstName || '',
        email: user.primaryEmailAddress?.emailAddress || ''
      }));
    }
  }, [isLoaded, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow editing subject and message
    if (name === 'subject' || name === 'message') {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Message sent! Check your email for a confirmation. We\'ll get back to you soon.' 
        });
        setFormData(prev => ({ ...prev, subject: '', message: '' }));
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to send message.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network error. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="back-link">← Back to App</Link>
          <h1>Contact Support</h1>
          <p className="meta">We're here to help!</p>
        </header>

        <section className="policy-summary">
          <div className="highlight-box">
            <h3>📬 Get in Touch</h3>
            <p>
              Have a question, issue, or feedback? Fill out the form below and we'll 
              respond as quickly as possible. You'll receive a confirmation email with 
              a copy of your message.
            </p>
          </div>
        </section>

        <section>
          <h2>Send Us a Message</h2>
          
          {status.message && (
            <div className={`status-message ${status.type}`}>
              {status.type === 'success' ? '✅' : '❌'} {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="support-form">
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Your Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="What can we help with?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                placeholder="Describe your question or issue in detail..."
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </section>

        <section>
          <h2>Prefer Email Directly?</h2>
          <p>
            You can also reach us directly at:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="support-email-link">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p>
            We typically respond within 1-2 business days.
          </p>
        </section>

        <section>
          <h2>Common Topics</h2>
          <ul>
            <li><strong>Account Issues:</strong> Password resets, email changes, account deletion</li>
            <li><strong>Billing & Subscriptions:</strong> Payments, refunds, plan changes</li>
            <li><strong>Technical Problems:</strong> Bugs, app crashes, sync issues</li>
            <li><strong>Feature Requests:</strong> Suggestions for new features or improvements</li>
            <li><strong>General Questions:</strong> How to use the app, workout advice</li>
          </ul>
        </section>

        <section>
          <h2>Helpful Links</h2>
          <ul>
            <li><Link to="/refund">Refund Policy</Link> - Information about refunds and cancellations</li>
            <li><Link to="/privacy">Privacy Policy</Link> - How we handle your data</li>
            <li><Link to="/terms-and-conditions">Terms & Conditions</Link> - Service terms</li>
            <li><Link to="/pricing">Pricing</Link> - Subscription plans and features</li>
          </ul>
        </section>

        <footer className="legal-footer">
          <div className="footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms-and-conditions">Terms & Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/refund">Refund Policy</Link>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Kata.fit. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default SupportPage;
